import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/** Compile state of a draft, derived from its newest `writer_versions` row. */
export type DraftCompileStatus =
  | 'none'
  | 'pending'
  | 'running'
  | 'success'
  | 'failed';

export interface WriterProjectOverview {
  id: string;
  title: string;
  updatedAt: string;
  /** Set once the draft has been submitted as a version of a published paper. */
  publishedPaperId: string | null;
  compileStatus: DraftCompileStatus;
  /** Timestamp of the newest version row, if any. */
  lastVersionAt: string | null;
  versionCount: number;
}

type ProjectRow = {
  id: string;
  title: string;
  updated_at: string;
  published_paper_id: string | null;
};

type VersionRow = {
  id: string;
  project_id: string;
  compile_status: string;
  created_at: string;
};

const KNOWN: DraftCompileStatus[] = ['pending', 'running', 'success', 'failed'];

const normaliseStatus = (raw: string | null | undefined): DraftCompileStatus =>
  KNOWN.includes(raw as DraftCompileStatus) ? (raw as DraftCompileStatus) : 'none';

/**
 * Reduce a `created_at desc` ordered list of versions into the newest row per
 * project, plus a per-project row count.
 */
export function reduceNewestVersions(rows: VersionRow[]): Map<
  string,
  { status: DraftCompileStatus; createdAt: string; count: number }
> {
  const map = new Map<string, { status: DraftCompileStatus; createdAt: string; count: number }>();
  for (const row of rows) {
    const existing = map.get(row.project_id);
    if (!existing) {
      map.set(row.project_id, {
        status: normaliseStatus(row.compile_status),
        createdAt: row.created_at,
        count: 1,
      });
      continue;
    }
    existing.count += 1;
    // Defensive: do not rely on server ordering alone.
    if (new Date(row.created_at).getTime() > new Date(existing.createdAt).getTime()) {
      existing.status = normaliseStatus(row.compile_status);
      existing.createdAt = row.created_at;
    }
  }
  return map;
}

/**
 * Every active Writer draft of the signed-in user with its live compile state.
 *
 * There is no view joining `writer_projects` to its newest `writer_versions`
 * row, so this is deliberately two reads: projects first, then versions
 * filtered by those project ids, reduced client-side. RLS on both tables is
 * `is_writer_admin_owner(owner_id)`, the same admin flag that gates the page.
 *
 * Polling only ticks while a compile is actually in flight — the per-project
 * realtime channel used by `useWriterVersions` does not fit a list view.
 */
export function useWriterProjectsOverview() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<WriterProjectOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const { data: projectRows, error: projectError } = await supabase
      .from('writer_projects')
      .select('id, title, updated_at, published_paper_id')
      .eq('archived', false)
      .order('updated_at', { ascending: false })
      .limit(50);

    if (projectError) {
      setError(projectError.message);
      setLoading(false);
      return;
    }

    const rows = (projectRows ?? []) as ProjectRow[];
    if (rows.length === 0) {
      setProjects([]);
      setError(null);
      setLoading(false);
      return;
    }

    const { data: versionRows } = await supabase
      .from('writer_versions')
      .select('id, project_id, compile_status, created_at')
      .in(
        'project_id',
        rows.map((r) => r.id),
      )
      .order('created_at', { ascending: false })
      .limit(300);

    const newest = reduceNewestVersions((versionRows ?? []) as VersionRow[]);

    setProjects(
      rows.map((r) => {
        const v = newest.get(r.id);
        return {
          id: r.id,
          title: r.title,
          updatedAt: r.updated_at,
          publishedPaperId: r.published_paper_id,
          compileStatus: v?.status ?? 'none',
          lastVersionAt: v?.createdAt ?? null,
          versionCount: v?.count ?? 0,
        };
      }),
    );
    setError(null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const compileInFlight = useMemo(
    () => projects.some((p) => p.compileStatus === 'pending' || p.compileStatus === 'running'),
    [projects],
  );

  useEffect(() => {
    if (!compileInFlight) return;
    const timer = setInterval(() => {
      void load();
    }, 4000);
    return () => clearInterval(timer);
  }, [compileInFlight, load]);

  return { projects, loading, error, reload: load, compileInFlight };
}
